import { firestore } from 'app';
import { create } from 'zustand';
import { VideoCall, CallState, CallParticipant, CallSignal } from './videoCallTypes';
import { 
  subscribeToIncomingCalls, 
  subscribeToCall, 
  subscribeToSignals,
  updateCallStatus,
  acceptCall,
  declineCall,
  endCall,
  deleteSignal,
  initiateCall
} from './videoCallService';
import { subscribeToEndedSignals, subscribeToAnyEndedSignal } from './rtdbSignalingService';
import { webRTCManager } from './webRTCService';
import brain from 'brain';

// ─────────────────────────────────────────────────────────────────
// Per-incoming-call status watchers.
//
// The collection-level `subscribeToIncomingCalls` query
// (where status in ['initiating','ringing']) is meant to drop a
// call out of the snapshot the moment its status changes — but in
// practice the web's incoming-call modal still gets stuck after
// the user accepts/declines the SAME call on a NATIVE device.
//
// Root cause: the native app DOES NOT always update the Firestore
// `status` field when accepting/declining/ending. It primarily uses
// the Firebase Realtime Database (RTDB) signal channel
// (path: calls/{callId}/ended/{userId}) to tell the OTHER side the
// call is over. With Firestore `status` left at "ringing", the
// collection listener and the per-doc listener both think the call
// is still incoming.
//
// As a belt-and-braces fix we therefore subscribe to TWO things
// for every incoming call:
//   1. The call's specific Firestore doc (so a regular web-side
//      decline/end still works).
//   2. The RTDB ended/{userId} path for the current user (so an
//      end/decline coming from a native device dismisses the modal
//      immediately).
// Either channel firing drops the call from `incomingCalls`.
// ─────────────────────────────────────────────────────────────────
const incomingCallWatchers = new Map<string, () => void>();
// Captured from `subscribeToUserCalls(userId)` so per-call RTDB
// watchers know which `ended/{userId}` node to listen on.
let videoCallCurrentUserId: string | null = null;

const stopIncomingCallWatcher = (callId: string) => {
  const stop = incomingCallWatchers.get(callId);
  if (stop) {
    try { stop(); } catch (e) { console.warn('[VideoCall] Watcher cleanup failed', e); }
    incomingCallWatchers.delete(callId);
  }
};

// Helper function to wait for tracks to be ready and capturing
const waitForTracksReady = async (stream: MediaStream): Promise<void> => {
  console.log('[VideoCall] Waiting for tracks to be ready...');
  
  const trackPromises = stream.getTracks().map(track => {
    return new Promise<void>((resolve) => {
      // If track is already unmuted, resolve immediately
      if (!track.muted && track.readyState === 'live') {
        console.log(`[VideoCall] Track ${track.kind} already ready`);
        resolve();
        return;
      }
      
      // Wait for unmute event
      const handleUnmute = () => {
        console.log(`[VideoCall] Track ${track.kind} unmuted and ready`);
        track.removeEventListener('unmute', handleUnmute);
        resolve();
      };
      
      track.addEventListener('unmute', handleUnmute);
      
      // Timeout after 2 seconds to prevent hanging
      setTimeout(() => {
        track.removeEventListener('unmute', handleUnmute);
        console.log(`[VideoCall] Track ${track.kind} timeout, proceeding anyway`);
        resolve();
      }, 2000);
    });
  });
  
  await Promise.all(trackPromises);
  console.log('[VideoCall] All tracks ready');
};

// Helper function to create call message via backend
const createCallMessage = async (
  callId: string,
  conversationId: string,
  callerId: string,
  callerName: string,
  duration: number,
  status: 'completed' | 'missed' | 'declined'
) => {
  try {
    console.log('[VideoCallStore] ===== CREATING CALL MESSAGE =====');
    console.log('[VideoCallStore] callId:', callId);
    console.log('[VideoCallStore] conversationId:', conversationId);
    console.log('[VideoCallStore] callerId:', callerId);
    console.log('[VideoCallStore] callerName:', callerName);
    console.log('[VideoCallStore] duration:', duration);
    console.log('[VideoCallStore] status:', status);
    
    await brain.create_call_message({
      callId,
      conversationId,
      callerId,
      callerName,
      duration,
      status,
    });
    console.log('[VideoCallStore] ✅ Call message created successfully!');
  } catch (error) {
    console.error('[VideoCallStore] ❌ Failed to create call message:', error);
    throw error;
  }
};

interface CallStoreActions {
  // Call management
  setCurrentCall: (call: VideoCall | null) => void;
  setIncomingCalls: (calls: VideoCall[]) => void;
  addIncomingCall: (call: VideoCall) => void;
  removeIncomingCall: (callId: string) => void;
  setIsInitiating: (isInitiating: boolean) => void;
  setIsConnecting: (isConnecting: boolean) => void;
  setError: (error: string | null) => void;
  
  // Media management
  setLocalStream: (stream: MediaStream | null) => void;
  setRemoteStream: (userId: string, stream: MediaStream) => void;
  removeRemoteStream: (userId: string) => void;
  toggleVideo: () => void;
  toggleAudio: () => void;
  
  // UI state
  setShowIncomingCallModal: (show: boolean) => void;
  setShowCallInterface: (show: boolean) => void;
  
  // Call actions
  acceptIncomingCall: (callId: string, userId: string) => Promise<void>;
  declineIncomingCall: (callId: string, userId: string) => Promise<void>;
  endCurrentCall: (userId: string) => Promise<void>;
  
  // Subscriptions
  subscribeToUserCalls: (userId: string) => () => void;
  subscribeToActiveCall: (callId: string) => () => void;
  subscribeToCallSignals: (callId: string, userId: string) => () => void;
  
  // Cleanup
  cleanup: () => void;
  reset: () => void;
}

interface VideoCallState {
  incomingCalls: VideoCall[];
  currentCall: VideoCall | null;
  showIncomingCallModal: boolean;
  showCallInterface: boolean;
  localStream: MediaStream | null;
  remoteStreams: Record<string, MediaStream>;
  isAudioEnabled: boolean;
  isVideoEnabled: boolean;
  isConnecting: boolean;
  isInitiating: boolean;
  error: string | null;
  callStartTime: number | null; // Add this field to track when call actually connected
}

const initialState: VideoCallState = {
  incomingCalls: [],
  currentCall: null,
  showIncomingCallModal: false,
  showCallInterface: false,
  localStream: null,
  remoteStreams: {},
  isAudioEnabled: true,
  isVideoEnabled: true,
  isConnecting: false,
  isInitiating: false,
  error: null,
  callStartTime: null, // Initialize
};

export const useVideoCallStore = create<VideoCallState & CallStoreActions>((set, get) => ({
  ...initialState,
  
  // Call management
  setCurrentCall: (call) => set({ currentCall: call }),
  
  setIncomingCalls: (calls) => set({ incomingCalls: calls }),
  
  addIncomingCall: (call) => {
    // Reject obviously stale incoming calls. If the call was created
    // more than 60 seconds ago by the time we see it, the user has
    // already missed it (or it's wedged in 'ringing' because the
    // native app forgot to update its Firestore status). Either
    // way, don't pop the modal.
    try {
      const created = (call as any)?.createdAt;
      let createdMs: number | null = null;
      if (created) {
        if (typeof created === 'number') createdMs = created;
        else if (typeof created === 'string') createdMs = new Date(created).getTime();
        else if (created instanceof Date) createdMs = created.getTime();
        else if (typeof (created as any).toDate === 'function') createdMs = (created as any).toDate().getTime();
        else if (typeof (created as any).seconds === 'number') createdMs = (created as any).seconds * 1000;
      }
      if (createdMs && Number.isFinite(createdMs)) {
        const ageMs = Date.now() - createdMs;
        if (ageMs > 60_000) {
          console.warn('[VideoCall] Ignoring stale incoming call (age=' + Math.round(ageMs / 1000) + 's)', call.id);
          // Best-effort: try to mark it ended server-side so it
          // disappears for everyone — but don't block on it.
          (async () => {
            try {
              await updateCallStatus(call.id, 'ended' as any);
            } catch (e) {
              console.warn('[VideoCall] Failed to clean up stale call', call.id, e);
            }
          })();
          return;
        }
      }
    } catch (e) {
      console.warn('[VideoCall] Stale-age check failed', e);
    }

    const { incomingCalls } = get();
    if (!incomingCalls.find(c => c.id === call.id)) {
      set({ 
        incomingCalls: [...incomingCalls, call],
        showIncomingCallModal: true
      });

      // Start a per-call watcher so we drop the modal IMMEDIATELY
      // when the call ends/declines on a NATIVE device — even if the
      // collection listener misses the snapshot update. We attach
      // multiple listeners to be bullet-proof against the native
      // signalling mode of the moment:
      //   1. Firestore call doc (status changes from native-via-
      //      backend or another web tab).
      //   2. RTDB ended/{currentUserId} (when the OTHER party ends
      //      the call — signal targets us).
      //   3. RTDB ended/* (any participant ending — catches the
      //      multi-device case where the same user accepts/ends on
      //      their phone and the native app writes the signal to
      //      the OTHER party's bucket).
      //   4. Hard timeout (60s of unanswered ringing → auto-dismiss).
      //      A real call never rings for more than ~30s; this stops
      //      the modal getting wedged for hours after a missed call.
      if (!incomingCallWatchers.has(call.id)) {
        const stopDocWatcher = subscribeToCall(call.id, (latest) => {
          // Doc deleted entirely — definitely no longer incoming.
          if (!latest) {
            get().removeIncomingCall(call.id);
            return;
          }
          const status = (latest.status || '').toString();
          // Anything other than the two "ringing" states means the
          // call is no longer incoming (it was accepted, declined,
          // ended, missed, completed, connected, in_progress, …).
          if (status !== 'ringing' && status !== 'initiating') {
            get().removeIncomingCall(call.id);
          }
        });

        // RTDB targeted ended-signal listener (current user's bucket).
        let stopRtdbWatcher: (() => void) | null = null;
        if (videoCallCurrentUserId) {
          try {
            stopRtdbWatcher = subscribeToEndedSignals(
              call.id,
              videoCallCurrentUserId,
              (fromUserId) => {
                console.log('[VideoCall] RTDB ended signal (targeted) for', call.id, 'from', fromUserId);
                get().removeIncomingCall(call.id);
              },
            );
          } catch (e) {
            console.warn('[VideoCall] Failed to start targeted RTDB ended watcher', e);
          }
        }

        // RTDB parent ended/* listener — fires for ANY participant's
        // end signal. Critical for the "user accepts on their phone
        // while web is also ringing" case.
        let stopRtdbAnyWatcher: (() => void) | null = null;
        try {
          stopRtdbAnyWatcher = subscribeToAnyEndedSignal(
            call.id,
            (targetUserId) => {
              console.log('[VideoCall] RTDB ended/* signal for', call.id, 'target=', targetUserId);
              get().removeIncomingCall(call.id);
            },
          );
        } catch (e) {
          console.warn('[VideoCall] Failed to start parent RTDB ended watcher', e);
        }

        // Hard timeout fallback — ~60s of unanswered ringing.
        const RINGING_TIMEOUT_MS = 60_000;
        const timeoutHandle: ReturnType<typeof setTimeout> = setTimeout(() => {
          // Check if still in the list — could've been removed via a
          // watcher firing in the meantime.
          const stillRinging = get().incomingCalls.some((c) => c.id === call.id);
          if (stillRinging) {
            console.warn('[VideoCall] Ringing timeout (>60s) — auto-dismissing stale incoming call', call.id);
            get().removeIncomingCall(call.id);
          }
        }, RINGING_TIMEOUT_MS);

        incomingCallWatchers.set(call.id, () => {
          try { stopDocWatcher(); } catch { /* noop */ }
          try { stopRtdbWatcher?.(); } catch { /* noop */ }
          try { stopRtdbAnyWatcher?.(); } catch { /* noop */ }
          try { clearTimeout(timeoutHandle); } catch { /* noop */ }
        });
      }
    }
  },
  
  removeIncomingCall: (callId) => {
    const { incomingCalls } = get();
    const updatedCalls = incomingCalls.filter(call => call.id !== callId);
    // Stop watching this call.
    stopIncomingCallWatcher(callId);
    set({ 
      incomingCalls: updatedCalls,
      showIncomingCallModal: updatedCalls.length > 0
    });
  },
  
  setIsInitiating: (isInitiating) => set({ isInitiating }),
  setIsConnecting: (isConnecting) => set({ isConnecting }),
  setError: (error) => set({ error }),
  
  // Media management
  setLocalStream: (stream) => {
    const { localStream } = get();
    // Stop previous stream if exists
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
    }
    set({ localStream: stream });
  },
  
  setRemoteStream: (userId, stream) => {
    console.log('[VideoCallStore] 🎥 Remote stream received for user:', userId);
    console.log('[VideoCallStore] Stream tracks:', stream.getTracks().map(t => t.kind));
    const { remoteStreams } = get();
    set({ 
      remoteStreams: { ...remoteStreams, [userId]: stream }
    });
    console.log('[VideoCallStore] Total remote streams:', Object.keys({ ...remoteStreams, [userId]: stream }).length);
  },
  
  removeRemoteStream: (userId) => {
    const { remoteStreams } = get();
    const { [userId]: removed, ...rest } = remoteStreams;
    if (removed) {
      removed.getTracks().forEach(track => track.stop());
    }
    set({ remoteStreams: rest });
  },
  
  toggleVideo: async () => {
    const { localStream, isVideoEnabled } = get();
    
    if (!localStream) return;
    
    if (isVideoEnabled) {
      // Turning video OFF - just disable the track
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = false;
        set({ isVideoEnabled: false });
      }
    } else {
      // Turning video ON - get a fresh video track
      try {
        const newStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false // Don't replace audio track
        });
        
        const newVideoTrack = newStream.getVideoTracks()[0];
        const oldVideoTrack = localStream.getVideoTracks()[0];
        
        // Stop the old video track
        if (oldVideoTrack) {
          oldVideoTrack.stop();
        }
        
        // Create a NEW MediaStream with existing audio + new video
        // This ensures React detects the change
        const audioTracks = localStream.getAudioTracks();
        const updatedStream = new MediaStream([...audioTracks, newVideoTrack]);
        
        // Update all peer connections with the new track
        webRTCManager.replaceVideoTrack(newVideoTrack);
        
        set({ 
          isVideoEnabled: true,
          localStream: updatedStream // New stream instance triggers re-render
        });
      } catch (error) {
        console.error('[VideoCall] Error re-enabling video:', error);
        set({ error: 'Failed to enable camera' });
      }
    }
  },
  
  toggleAudio: () => {
    const { localStream, isAudioEnabled } = get();
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !isAudioEnabled;
        set({ isAudioEnabled: !isAudioEnabled });
      }
    }
  },
  
  // UI state
  setShowIncomingCallModal: (show) => set({ showIncomingCallModal: show }),
  setShowCallInterface: (show) => set({ showCallInterface: show }),
  
  // Call management
  initiateAndSetCall: async (callData, currentUser) => {
    let hasSetupWebRTC = false; // Prevent duplicate setup
    
    try {
      set({ 
        isInitiating: true,
        showCallInterface: true // Show interface immediately when initiating
      });
      
      // Create the call via backend (status will be 'ringing')
      const callId = await initiateCall(callData, currentUser);
      
      console.log('[VideoCall] Call created with ID:', callId);
      
      // Fetch the call document from Firestore to get the complete participant list
      // (backend may have added admin users for admin_call type)
      const { doc, getDoc } = await import('firebase/firestore');
      const callDocRef = doc(firestore, 'video_calls', callId);
      const callDocSnap = await getDoc(callDocRef);
      const callFromFirestore = callDocSnap.data();
      const actualParticipants = callFromFirestore?.participants || callData.participants;
      
      console.log('[VideoCall] Original callData.participants:', callData.participants);
      console.log('[VideoCall] Actual participants from Firestore:', actualParticipants);
      
      // IMMEDIATELY set up WebRTC and send offer (don't wait for status change)
      // This is required for native app to receive the offer while ringing
      try {
        console.log('[VideoCall] Setting up WebRTC immediately after call creation...');
        
        // Get local media stream
        let stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true
        });
        
        // Explicitly enable all tracks to ensure they're not muted
        stream.getTracks().forEach(track => {
          track.enabled = true;
          console.log(`[VideoCall] Initial track ${track.kind} - enabled: ${track.enabled}, muted: ${track.muted}, readyState: ${track.readyState}`);
        });
        
        // Brief delay to let tracks initialize
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Check if any video tracks are still muted - if so, recreate the stream
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack && videoTrack.muted) {
          console.warn('[VideoCall] Video track is muted, requesting fresh stream');
          stream.getTracks().forEach(track => track.stop());
          stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: true
          });
          stream.getTracks().forEach(track => {
            track.enabled = true;
            console.log(`[VideoCall] Fresh track ${track.kind} - enabled: ${track.enabled}, muted: ${track.muted}, readyState: ${track.readyState}`);
          });
        }
        
        set({ localStream: stream });
        console.log('[VideoCall] Got local stream, tracks ready');
        
        // Set up webRTCManager with local stream and remote stream callback
        webRTCManager.setLocalStream(stream);
        webRTCManager.setOnRemoteStream((userId, remoteStream) => {
          console.log('[VideoCall] Received remote stream from:', userId);
          get().setRemoteStream(userId, remoteStream);
        });
        
        // Get OTHER participants (exclude current user) from the actual Firestore data
        const otherParticipants = actualParticipants.filter((p: string) => p !== currentUser.id);
        
        console.log('[VideoCall] currentUser object:', currentUser);
        console.log('[VideoCall] currentUser.id:', currentUser.id);
        console.log('[VideoCall] otherParticipants to send offers to:', otherParticipants);
        
        // Initialize RTDB signaling (real-time listeners)
        console.log('[VideoCall] Initializing RTDB signaling...');
        await webRTCManager.initializeRTDBSignaling(callId, currentUser.id);
        
        // Create peer connections and send offers to all participants
        for (const participantId of otherParticipants) {
          console.log('[VideoCall] 🔵 INITIATOR creating peer connection and sending offer to:', participantId);
          await webRTCManager.createPeerConnection(
            callId,
            currentUser.id,
            participantId,
            true // initiator - will send OFFER immediately
          );
        }
        
        hasSetupWebRTC = true;
        console.log('[VideoCall] WebRTC setup complete - offers sent via RTDB');
        
      } catch (error) {
        console.error('[VideoCall] Error setting up WebRTC:', error);
        set({ error: 'Failed to access camera/microphone' });
      }
      
      // Subscribe to the call for status updates
      const unsubscribe = subscribeToCall(callId, async (call) => {
        if (call) {
          set({ 
            currentCall: call,
            showCallInterface: true,
            isInitiating: false
          });
          
          // When call becomes 'connected' or 'in_progress' (native uses in_progress), record the start time locally
          if ((call.status === 'connected' || call.status === 'in_progress') && !get().callStartTime) {
            set({ callStartTime: Date.now() });
          }
          
          // Handle call status changes
          if (call.status === 'ended' || call.status === 'declined') {
            set({ 
              currentCall: null,
              showCallInterface: false
            });
            get().cleanup();
            unsubscribe();
          }
        }
      });
      
      return callId;
    } catch (error) {
      console.error('Error initiating call:', error);
      set({ error: 'Failed to initiate call', isInitiating: false });
      throw error;
    }
  },
  
  acceptIncomingCall: async (callId, userId) => {
    try {
      console.log('[VideoCall] Accepting call:', callId, 'for user:', userId);
      set({ 
        isConnecting: true, 
        error: null,
        showCallInterface: true, // Show interface immediately when accepting
        showIncomingCallModal: false // Hide incoming call modal
      });
      
      // Accept the call (updates status to 'connecting')
      await acceptCall(callId, userId);
      console.log('[VideoCall] Call accepted successfully');
      
      // Subscribe to the call for real-time updates
      let hasSetupWebRTC = false;
      const unsubscribe = subscribeToCall(callId, async (call) => {
        if (call) {
          set({ 
            currentCall: call,
            showCallInterface: true, // Ensure it stays visible
            showIncomingCallModal: false,
            isConnecting: false
          });
          
          // When call becomes 'connected' or 'in_progress' (native uses in_progress), record the start time locally
          if ((call.status === 'connected' || call.status === 'in_progress') && !get().callStartTime) {
            set({ callStartTime: Date.now() });
          }
          
          // When call becomes 'connecting', set up WebRTC
          if (call.status === 'connecting' && !hasSetupWebRTC) {
            hasSetupWebRTC = true;
            console.log('[VideoCall] Call connecting, setting up WebRTC...');
            
            try {
              // Get local media stream
              let stream = await navigator.mediaDevices.getUserMedia({
                video: true,
                audio: true
              });
              
              // Explicitly enable all tracks to ensure they're not muted
              stream.getTracks().forEach(track => {
                track.enabled = true;
                console.log(`[VideoCall] Initial track ${track.kind} - enabled: ${track.enabled}, muted: ${track.muted}, readyState: ${track.readyState}`);
              });
              
              // Brief delay to let tracks initialize
              await new Promise(resolve => setTimeout(resolve, 100));
              
              // Check if any video tracks are still muted - if so, recreate the stream
              const videoTrack = stream.getVideoTracks()[0];
              if (videoTrack && videoTrack.muted) {
                console.warn('[VideoCall] Video track is muted, requesting fresh stream');
                // Stop old stream
                stream.getTracks().forEach(track => track.stop());
                
                // Get a fresh stream
                stream = await navigator.mediaDevices.getUserMedia({
                  video: true,
                  audio: true
                });
                
                stream.getTracks().forEach(track => {
                  track.enabled = true;
                  console.log(`[VideoCall] Fresh track ${track.kind} - enabled: ${track.enabled}, muted: ${track.muted}, readyState: ${track.readyState}`);
                });
              }
              
              set({ localStream: stream });
              console.log('[VideoCall] Got local stream, tracks ready');
              
              // Set up webRTCManager with local stream and remote stream callback
              webRTCManager.setLocalStream(stream);
              webRTCManager.setOnRemoteStream((userId, remoteStream) => {
                console.log('[VideoCall] Received remote stream from:', userId);
                get().setRemoteStream(userId, remoteStream);
              });
              
              // Create peer connections for all other participants
              const otherParticipants = call.participants.filter(p => p !== userId);
              
              console.log('[VideoCall] currentUser userId:', userId);
              console.log('[VideoCall] otherParticipants:', otherParticipants);
              
              // Initialize RTDB signaling FIRST (real-time listeners)
              console.log('[VideoCall] Initializing RTDB signaling for acceptor...');
              await webRTCManager.initializeRTDBSignaling(call.id, userId);
              
              for (const participantId of otherParticipants) {
                console.log('[VideoCall] 🟢 ACCEPTOR creating peer connection for:', participantId);
                await webRTCManager.createPeerConnection(
                  call.id,
                  userId,
                  participantId,
                  false // not initiator - will wait for OFFER and send ANSWER
                );
              }
              
              console.log('[VideoCall] WebRTC setup complete with RTDB signaling');
            } catch (error) {
              console.error('[VideoCall] Error setting up WebRTC:', error);
              set({ error: 'Failed to access camera/microphone' });
            }
          }
          
          // Handle call status changes
          if (call.status === 'ended' || call.status === 'declined') {
            set({ 
              currentCall: null,
              showCallInterface: false
            });
            get().cleanup();
            unsubscribe();
          }
        }
      });
      
      // Remove from incoming calls
      get().removeIncomingCall(callId);
    } catch (error) {
      console.error('Error accepting call:', error);
      set({ error: 'Failed to accept call', isConnecting: false });
    }
  },
  
  declineIncomingCall: async (callId, userId) => {
    try {
      await declineCall(callId, userId);
      get().removeIncomingCall(callId);
    } catch (error) {
      console.error('Error declining call:', error);
      set({ error: 'Failed to decline call' });
    }
  },
  
  endCurrentCall: async (userId) => {
    try {
      console.log('[VideoCallStore] 🛑 endCurrentCall called for userId:', userId);
      const { currentCall } = get();
      if (currentCall) {
        console.log('[VideoCallStore] Current call exists:', currentCall.id, 'status:', currentCall.status);
        let status: 'completed' | 'missed' | 'declined' = 'completed';
        
        if (currentCall.status === 'ringing' || currentCall.status === 'initiating') {
          status = 'missed';
        }
        
        console.log('[VideoCallStore] Calling endCall API with status:', status);
        // End the call (backend will calculate duration)
        await endCall(currentCall.id, userId);
        
        // Wait longer for Firestore to update with the duration
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Fetch the updated call document to get the calculated duration and conversationId
        try {
          const { doc, getDoc, updateDoc } = await import('firebase/firestore');
          const { firebaseApp, firestore } = await import('app');
          
          const callDocRef = doc(firestore, 'video_calls', currentCall.id);
          const callDoc = await getDoc(callDocRef);
          
          if (callDoc.exists()) {
            const callData = callDoc.data();
            const duration = callData.duration || 0;
            const conversationId = callData.conversationId;
            const messageCreated = callData.messageCreated || false;
            
            console.log('[VideoCallStore] Call ended with duration:', duration, 'conversationId:', conversationId, 'messageCreated:', messageCreated);
            
            // Only create message if it hasn't been created yet
            if (messageCreated) {
              console.log('[VideoCallStore] ⏭️ Message already created, skipping');
            } else {
              console.log('[VideoCallStore] ✅ Message not yet created, proceeding to create it');
              
              // If conversationId exists in the call document, use it directly
              if (conversationId) {
                console.log('[VideoCallStore] ✅ Using conversationId from call document:', conversationId);
                await createCallMessage(
                  currentCall.id,
                  conversationId,
                  currentCall.callerId,
                  currentCall.callerName,
                  duration,
                  status
                );
              } else {
                // Fallback: Try to find a matching conversation (for calls not initiated from chat)
                console.log('[VideoCallStore] No conversationId in call document, attempting to find matching conversation');
                try {
                  const { collection, query, where, getDocs } = await import('firebase/firestore');
                  
                  // Find conversation with matching participants
                  const conversationsRef = collection(firestore, 'conversations');
                  const q = query(
                    conversationsRef,
                    where('participants', 'array-contains', userId)
                  );
                  
                  const snapshot = await getDocs(q);
                  let conversationFound = false;
                  
                  for (const docSnap of snapshot.docs) {
                    const conv = docSnap.data();
                    // Check if all participants match (exact set match)
                    const callParticipants = [...currentCall.participants].sort();
                    const convParticipants = [...conv.participants].sort();
                    
                    if (callParticipants.length === convParticipants.length &&
                        callParticipants.every((p, i) => p === convParticipants[i])) {
                      console.log('[VideoCallStore] Found matching conversation:', docSnap.id);
                      await createCallMessage(
                        currentCall.id,
                        docSnap.id,
                        currentCall.callerId,
                        currentCall.callerName,
                        duration,
                        status
                      );
                      conversationFound = true;
                      break;
                    }
                  }
                  
                  if (!conversationFound) {
                    console.warn('[VideoCallStore] No matching conversation found for call', currentCall.id, 'participants:', currentCall.participants);
                  }
                } catch (error) {
                  console.error('Error finding matching conversation:', error);
                }
              }
            }
          } else {
            console.error('[VideoCallStore] Call document not found after ending:', currentCall.id);
          }
        } catch (error) {
          console.error('Error fetching updated call document:', error);
        }
        
        set({ 
          currentCall: null,
          showCallInterface: false,
          isConnecting: false,
          callStartTime: null
        });
        
        // Cleanup streams
        get().cleanup();
      }
    } catch (error) {
      console.error('Error ending call:', error);
      set({ error: 'Failed to end call' });
    }
  },
  
  // Subscriptions
  subscribeToUserCalls: (userId) => {
    // Capture the current user id so per-call RTDB watchers
    // (started inside addIncomingCall) know which ended/{userId}
    // node to listen on. Module-level state because the watcher
    // map is also module-level.
    videoCallCurrentUserId = userId || null;
    const unsubscribe = subscribeToIncomingCalls(userId, (calls) => {
      // Filter out calls that are no longer incoming
      const incomingCalls = calls.filter(call => 
        call.status === 'initiating' || call.status === 'ringing'
      );
      
      const { incomingCalls: currentIncoming } = get();
      
      // Add new calls
      incomingCalls.forEach(call => {
        if (!currentIncoming.find(c => c.id === call.id)) {
          get().addIncomingCall(call);
        }
      });
      
      // Remove ended calls
      currentIncoming.forEach(call => {
        if (!incomingCalls.find(c => c.id === call.id)) {
          get().removeIncomingCall(call.id);
        }
      });
    });
    // Return a wrapped cleanup that also clears the captured uid
    // (so a re-login can refresh it next time).
    return () => {
      try { unsubscribe(); } catch { /* noop */ }
      videoCallCurrentUserId = null;
    };
  },
  
  subscribeToActiveCall: (callId) => {
    return subscribeToCall(callId, (call) => {
      if (call) {
        set({ currentCall: call });
        
        // Handle call status changes
        if (call.status === 'ended' || call.status === 'declined') {
          set({ 
            currentCall: null,
            showCallInterface: false,
            isConnecting: false
          });
          get().cleanup();
        } else if (call.status === 'connected' || call.status === 'in_progress') {
          set({ isConnecting: false });
        }
      }
    });
  },
  
  subscribeToCallSignals: (callId, userId) => {
    return subscribeToSignals(callId, userId, async (signals) => {
      console.log('[VideoCall] Received signals:', signals.length, 'signals');
      console.log('[VideoCall] Signal details:', signals.map(s => ({ type: s.type, from: s.fromUserId, to: s.toUserId })));
      
      // Process WebRTC signals sequentially
      for (const signal of signals) {
        console.log('[VideoCall] Processing signal:', signal.type, 'from:', signal.fromUserId);
        
        // Handle different signal types
        switch (signal.type) {
          case 'offer':
            console.log('[VideoCall] Processing offer from:', signal.fromUserId);
            await webRTCManager.handleOffer(
              callId,
              userId,
              signal.fromUserId,
              signal.data
            );
            break;
          case 'answer':
            console.log('[VideoCall] Processing answer from:', signal.fromUserId);
            await webRTCManager.handleAnswer(
              signal.fromUserId,
              signal.data
            );
            break;
          case 'ice-candidate':
            console.log('[VideoCall] Processing ICE candidate from:', signal.fromUserId);
            await webRTCManager.handleIceCandidate(
              signal.fromUserId,
              signal.data
            );
            break;
          case 'call-accept':
            if (signal.data?.acceptedBy !== userId) {
              
            }
            break;
          case 'call-decline':
            get().removeIncomingCall(callId);
            break;
          case 'call-end':
            if (signal.data?.endedBy !== userId) {
              set({ 
                currentCall: null,
                showCallInterface: false,
                isConnecting: false
              });
              get().cleanup();
            }
            break;
        }
        
        // Clean up processed signal
        deleteSignal(signal.id);
      }
    });
  },
  
  // Cleanup
  cleanup: async () => {
    console.log('[VideoCall] Starting cleanup...');
    const { localStream, remoteStreams } = get();
    
    // Close all WebRTC peer connections and RTDB signaling
    // This will also unregister participant and send ended signals via RTDB
    await webRTCManager.endCall();
    
    // Stop local stream tracks
    if (localStream) {
      console.log('[VideoCall] Stopping local stream tracks');
      localStream.getTracks().forEach(track => {
        console.log(`[VideoCall] Stopping local ${track.kind} track, enabled: ${track.enabled}, readyState: ${track.readyState}`);
        track.stop();
      });
    }
    
    // Stop remote stream tracks
    Object.entries(remoteStreams).forEach(([userId, stream]) => {
      console.log(`[VideoCall] Stopping remote stream from ${userId}`);
      stream.getTracks().forEach(track => {
        console.log(`[VideoCall] Stopping remote ${track.kind} track from ${userId}`);
        track.stop();
      });
    });
    
    set({ 
      localStream: null, 
      remoteStreams: {},
      callStartTime: null // Also reset here
    });
    
    console.log('[VideoCall] Cleanup complete with RTDB signaling');
  },
  
  reset: () => {
    get().cleanup();
    set(initialState);
  }
}));
